import * as React from 'react'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import { useAuth } from 'react-oidc-context'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import SearchBox from '../../../../components/SearchBox'
import LanguageStrings from '../../../../i18n/locales'
import { type RootState } from '../../../../store'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { fetchDeviceList } from '../../redux/actionCreators'
import { type customDeviceListState } from '../../redux/deviceListSlice'
import { Button } from '../../../../components/Button'
import EnhancedTable, { type Sort } from './DeviceTable'
import AddDevice from '../AddDevice/AddDevice'
import { Grid } from '@mui/material'

export interface State extends SnackbarOrigin {
  open: boolean
}

const DeviceListStrings = LanguageStrings().CustomDeviceCollectionStrings.DeviceListComponent
const DashboardStrings = LanguageStrings().Dashboard

const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}
const searchBoxComponent: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between'
}

function DeviceList (props: PropsFromRedux): JSX.Element {
  const [searchValue, setSearchValue] = React.useState('')
  const rowsPerPage = 10
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: '' })
  const [open, setOpen] = React.useState<any>(false)
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  const navigate = useNavigate()

  /* istanbul ignore next */
  const handleSearch = (): void => {
    setPage(1)
    props.fetchDeviceList(page, rowsPerPage, searchValue, sort.order, sort.orderBy)
  }
  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    /* istanbul ignore else */
    if (!operationPermissions.updated) {
      props.getOperationPermissions(auth.user?.profile.sub as string)
    }
  }, [])

  return (
    <div>
      <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading' >
        <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
          onClick={() => { navigate(-1) }}
        />
        <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
          <Link aria-label='navBreadcrumb' className='assetmgmtli' data-testid="bc-link-1" underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
            {DashboardStrings.HOME}
          </Link>
          <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{DeviceListStrings.CustomDeviceTemplate}</Typography>
        </Breadcrumbs>
      </div>
      <div className='ArrowDeviceList'>
        <div>
          <span data-testid='deviceHistoryDetails' className='deviceMgtDevicelist' style={{ fontWeight: '700', fontSize: '24px', fontFamily: 'Honeywell Sans Web' }}>{DeviceListStrings.CustomDeviceTemplate}</span>
        </div>
      </div>
      <div className='configurations-table-heading'>
        <h5 style={{ fontWeight: '700', fontSize: '16px', fontFamily: 'Honeywell Sans Web', letterSpacing: '2.5px', textTransform: 'uppercase' }} >{DeviceListStrings.CustomDeviceTemplateList}</h5>
      </div>
      <Grid container style={style} justifyContent={'space-between'}>
        <>
          <Grid item style={searchBoxComponent} xs={6} sm={6} md={4} lg={3} >
            <SearchBox
              sx={{ width: '100%', paddingRight: '0px' }}
              containerStyle={{ width: '100%' }}
              placeholder={DeviceListStrings.Search}
              immediate={true}
              emptyEnterFallback={() => { props.fetchDeviceList(page, rowsPerPage, searchValue, sort.order, sort.orderBy) }}
              searchVal={searchValue}
              setSearchVal={setSearchValue}
              performAction={handleSearch}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4} lg={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <>
              {
                operationPermissions.permissions.can_create_tiq_configurations
                  ? <>
                      <Button sx={{ margin: '10px 17px', padding: { xs: '6px 5px', lg: '6px 20px' } }} onClick={() => { setOpen(true) }} width="fit-content" dataTestId="create-device-btn">{DeviceListStrings.CreateDeviceTemplate}</Button>
                    </>
                  : <></>
              }
            </>
          </Grid>
          <Grid item xs={12}>
            <EnhancedTable
              searchVal={searchValue}
              page={page}
              rowsPerPage={rowsPerPage}
              sort={sort}
              setPage={setPage}
              setSort={setSort}
              performAction={() => { props.fetchDeviceList(page, rowsPerPage, searchValue, sort.order, sort.orderBy) }}
          />
          </Grid>
        </>
      </Grid>
      { open === true &&
        <>
          <AddDevice isOpen={open} closePopup={(params?: any) => { setOpen(params) }} />
        </>
      }
    </div>
  )
}

interface DispatchToProps {
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => void
  getOperationPermissions: (sub: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => dispatch(fetchDeviceList(PageNumber, PageItems, searchKey, SortParams, sortField)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  customDeviceList: customDeviceListState
  grandParentBreadcrumb: string
}
const mapStateToProps = (state: RootState): StateToProps => ({
  customDeviceList: state.customDeviceList,
  grandParentBreadcrumb: state.grandParentBreadcrumb
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DeviceList)
